import { useCallback, useEffect, useRef, useState } from "react";
import {
    AlertTriangle,
    Download,
    ExternalLink,
    FileBadge,
    LoaderCircle,
    RotateCw,
    Upload,
} from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";

const API = import.meta.env.VITE_API_URL || "http://localhost:9000";

export interface ConditionReport {
    id: string;
    status: string;
    fileName: string | null;
    publicUrl: string | null;
    createdAt: string;
    updatedAt: string;
}

export function ConditionReportTab({ vehicleId }: { vehicleId: string | null }) {
    const [report, setReport] = useState<ConditionReport | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [isRetrying, setIsRetrying] = useState(false);
    const uploadInputRef = useRef<HTMLInputElement | null>(null);
    // Guards against a slow response for a previous vehicle overwriting the current one
    const requestSeq = useRef(0);

    const loadReport = useCallback(async () => {
        const seq = ++requestSeq.current;
        if (!vehicleId) {
            setReport(null);
            setLoadError(false);
            setIsLoading(false);
            return;
        }
        setIsLoading(true);
        try {
            const response = await fetch(
                `${API}/api/admin/condition-reports?vehicleId=${encodeURIComponent(vehicleId)}`,
                { credentials: "include" },
            );
            if (seq !== requestSeq.current) return;
            if (!response.ok) {
                setReport(null);
                setLoadError(true);
                return;
            }
            const data = await response.json();
            if (seq !== requestSeq.current) return;
            setReport(Array.isArray(data) && data.length > 0 ? data[0] : null);
            setLoadError(false);
        } catch (err) {
            console.error("Failed to fetch condition report:", err);
            if (seq === requestSeq.current) {
                setReport(null);
                setLoadError(true);
            }
        } finally {
            if (seq === requestSeq.current) {
                setIsLoading(false);
            }
        }
    }, [vehicleId]);

    useEffect(() => {
        void loadReport();
    }, [loadReport]);

    const handleDownloadOriginal = async () => {
        if (!report) return;
        try {
            setIsDownloading(true);
            const response = await fetch(`${API}/api/admin/condition-reports/${report.id}/original-url`, {
                credentials: "include",
            });
            if (!response.ok) throw new Error("Kunne ikke hente nedlastingslenke");

            const { downloadUrl } = await response.json();
            const link = document.createElement("a");
            link.href = downloadUrl;
            link.download = report.fileName || "tilstandsrapport.pdf";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (err) {
            console.error("Nedlasting feilet:", err);
            alert("Kunne ikke laste ned originalen.");
        } finally {
            setIsDownloading(false);
        }
    };

    const handleRedactedUpload = async (file: File) => {
        if (!report) return;
        try {
            setIsUploading(true);
            const formData = new FormData();
            formData.append("file", file);

            const response = await fetch(`${API}/api/admin/condition-reports/${report.id}/redacted-upload`, {
                method: "POST",
                credentials: "include",
                body: formData,
            });
            if (!response.ok) throw new Error("Kunne ikke laste opp sladdet PDF");

            await loadReport();
            alert("Sladdet PDF er lastet opp og publisert på auksjonssiden.");
        } catch (err) {
            console.error("Opplasting feilet:", err);
            alert("Kunne ikke laste opp sladdet PDF.");
        } finally {
            setIsUploading(false);
            if (uploadInputRef.current) {
                uploadInputRef.current.value = "";
            }
        }
    };

    const handleRetry = async () => {
        if (!report) return;
        if (
            report.status === "ready" &&
            !window.confirm(
                "Rapporten er allerede publisert. Kjøre automatisk sladding på nytt? Den publiserte versjonen fjernes til jobben er ferdig.",
            )
        ) {
            return;
        }

        try {
            setIsRetrying(true);
            const response = await fetch(`${API}/api/admin/condition-reports/${report.id}/retry`, {
                method: "POST",
                credentials: "include",
            });
            if (!response.ok) throw new Error("Kunne ikke starte sladding på nytt");
            await loadReport();
        } catch (err) {
            console.error("Retry feilet:", err);
            alert("Kunne ikke starte sladding på nytt.");
        } finally {
            setIsRetrying(false);
        }
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case "ready":
                return <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20">Publisert</Badge>;
            case "processing":
                return <Badge className="bg-amber-500/10 text-amber-700 border-amber-500/20">Behandles</Badge>;
            case "failed":
                return <Badge className="bg-red-500/10 text-red-600 border-red-500/20">Feilet</Badge>;
            default:
                return <Badge variant="muted">{status}</Badge>;
        }
    };

    if (isLoading) {
        return (
            <div className="flex h-[200px] items-center justify-center">
                <LoaderCircle className="h-6 w-6 animate-spin text-primary/20" />
            </div>
        );
    }

    if (loadError) {
        return (
            <Card className="border-red-200 bg-red-50/50 shadow-sm">
                <CardContent className="px-6 py-8 text-center space-y-3">
                    <AlertTriangle className="h-6 w-6 mx-auto text-red-600" />
                    <p className="text-sm text-red-700">Kunne ikke laste tilstandsrapporten.</p>
                    <Button variant="outline" size="sm" onClick={() => void loadReport()}>
                        Prøv igjen
                    </Button>
                </CardContent>
            </Card>
        );
    }

    if (!report) {
        return (
            <Card className="border-slate-200/60 shadow-sm">
                <CardContent className="px-6 py-12 text-center text-slate-500">
                    <FileBadge className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                    Selger har ikke lastet opp tilstandsrapport for denne auksjonen.
                </CardContent>
            </Card>
        );
    }

    return (
        <div className="space-y-4">
            <input
                ref={uploadInputRef}
                type="file"
                accept="application/pdf,.pdf"
                className="hidden"
                onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (file) {
                        void handleRedactedUpload(file);
                    }
                }}
            />

            {report.status === "failed" && (
                <Card className="border-red-200 bg-red-50/50 shadow-sm">
                    <CardContent className="p-4 flex items-center gap-3">
                        <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0" />
                        <p className="text-sm text-red-700">
                            Automatisk sladding feilet, og rapporten er <span className="font-semibold">ikke publisert</span>.
                            Last ned originalen, sladd VIN og regnummer manuelt, og last opp den sladdede PDF-en.
                        </p>
                    </CardContent>
                </Card>
            )}

            <Card className="border-slate-200/60 shadow-sm">
                <CardContent className="p-6 space-y-5">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
                        <div className="flex items-center gap-3 min-w-0">
                            <div className="h-10 w-10 rounded-xl bg-slate-100 flex items-center justify-center text-slate-500 flex-shrink-0">
                                <FileBadge className="h-5 w-5" />
                            </div>
                            <div className="min-w-0">
                                <p className="font-medium text-slate-900 truncate">{report.fileName || "tilstandsrapport.pdf"}</p>
                                <p className="text-xs text-slate-500">
                                    Lastet opp {new Date(report.createdAt).toLocaleDateString("nb-NO")}{" "}
                                    {new Date(report.createdAt).toLocaleTimeString("nb-NO", { hour: "2-digit", minute: "2-digit" })}
                                </p>
                            </div>
                        </div>
                        {getStatusBadge(report.status)}
                    </div>

                    <p className="text-sm text-slate-500">
                        Opplastede tilstandsrapporter sladdes automatisk for VIN og regnummer før de publiseres
                        offentlig på auksjonssiden. Originalen er kun tilgjengelig her.
                    </p>

                    <div className="flex flex-wrap items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            className="gap-2"
                            disabled={isDownloading}
                            onClick={() => void handleDownloadOriginal()}
                        >
                            {isDownloading ? (
                                <LoaderCircle className="h-4 w-4 animate-spin" />
                            ) : (
                                <Download className="h-4 w-4" />
                            )}
                            Last ned original (usladdet)
                        </Button>

                        {report.publicUrl && (
                            <Button
                                variant="outline"
                                size="sm"
                                className="gap-2"
                                onClick={() => window.open(report.publicUrl!, "_blank", "noopener")}
                            >
                                <ExternalLink className="h-4 w-4" />
                                Åpne publisert PDF
                            </Button>
                        )}

                        <Button
                            variant="outline"
                            size="sm"
                            className="gap-2 border-emerald-200 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800"
                            disabled={isUploading}
                            onClick={() => uploadInputRef.current?.click()}
                        >
                            {isUploading ? (
                                <LoaderCircle className="h-4 w-4 animate-spin" />
                            ) : (
                                <Upload className="h-4 w-4" />
                            )}
                            {report.status === "ready" ? "Erstatt med manuelt sladdet PDF" : "Last opp manuelt sladdet PDF"}
                        </Button>

                        {report.status === "processing" && (
                            <Button
                                variant="outline"
                                size="sm"
                                className="gap-2"
                                onClick={() => void loadReport()}
                            >
                                <RotateCw className="h-4 w-4" />
                                Oppdater status
                            </Button>
                        )}

                        <Button
                            variant="outline"
                            size="sm"
                            className="gap-2"
                            disabled={isRetrying}
                            onClick={() => void handleRetry()}
                        >
                            {isRetrying ? (
                                <LoaderCircle className="h-4 w-4 animate-spin" />
                            ) : (
                                <RotateCw className="h-4 w-4" />
                            )}
                            Kjør automatisk sladding på nytt
                        </Button>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
